import { inject, Injectable } from "@angular/core";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { map, catchError, of, switchMap, mergeMap, withLatestFrom } from "rxjs";
import { Store } from "@ngrx/store";
import { AppState } from "../../common/state/root";
import { selectUser } from "../../common/state/user/user.selectors";
import { IOrganisation, IOrganisationUser, ToastType } from "../../common/models";
import { ICopilotUser } from '../../common/models/user.interface';
import { CustomerService } from "../services/customer.service";
import { AccountActions } from "./customer.actions";
import { ToastService } from "../../common/services/toast/toast.service";


@Injectable()
export class AccountEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store<AppState>);
  private accountService = inject(CustomerService);
  private toastService = inject(ToastService);

  loadCurrentUsers$ = createEffect(() =>  this.actions$.pipe(
    ofType(AccountActions.loadCurrentUsers),
    withLatestFrom(this.store$.select(selectUser)),
    switchMap(([action, user]) => {
      return of(AccountActions.loadUsers({ organisation: { id: user!.organisation_id} as IOrganisation}))
    })
    )
  );
  
  loadUsers$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.loadUsers),
    switchMap((action) => {
      return this.accountService.getUsers(action.organisation.id).pipe(
        map(users => AccountActions.loadUsersSuccess({ organisation: action.organisation, users: (users as IOrganisationUser[]) })),    // On success
        catchError(error => of(AccountActions.loadUsersError({organisation: action.organisation, error})))  // On failure
      )
    }),
  )
  );

  createUser$ = createEffect(() =>  this.actions$.pipe(
    ofType(AccountActions.createUser),
    switchMap((action) => {
      return this.accountService.addUser(action.user).pipe(
        map(user => {
          this.toastService.showToast("Created user successfully", ToastType.SUCCESS)
          return AccountActions.createUserSuccess({ user: (user as IOrganisationUser)})
        }),
        catchError(error => {
          this.toastService.showToast("Failed to create user", ToastType.ERROR)
          return of(AccountActions.createUserError({user: action.user, error}))
        }) 
      )
    }))
  );
  
  editUser$ = createEffect(() =>  this.actions$.pipe(
    ofType(AccountActions.editUser),
    switchMap((action) => {
      return this.accountService.updateUser(action.user).pipe(
        map(user => {
          this.toastService.showToast("Updated user successfully", ToastType.SUCCESS)
          return AccountActions.editUserSuccess({ user: (user as IOrganisationUser)})
        }),
        catchError(error => {
          this.toastService.showToast("Failed to edit user", ToastType.ERROR)
          return of(AccountActions.editUserError({user: action.user, error}))
        }) 
      )
    })
    )
  );
  

  loadCustomers$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.loadCustomers),
    withLatestFrom(this.store$.select(selectUser)),
    switchMap(([_action, user]) => {
      return this.accountService.getDistributees(user!.organisation_id).pipe(
        map(distributes => AccountActions.loadCustomersSuccess({ customers: distributes as any})),
        catchError(error => of(AccountActions.loadCustomersFailure({ error })))
      );
    })
  ));

  updateCustomerStatus$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.updateCustomerStatus),
    switchMap((action) => {
      return this.accountService.updateOrganisationStatus(action.organisation, action.status).pipe(
        map(organisation => {
          this.toastService.showToast("Successfully updated customer status", ToastType.SUCCESS)
          return AccountActions.updateCustomerStatusSuccess({ organisation: organisation})
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update customer status", ToastType.ERROR)
          return of(AccountActions.updateCustomerStatusError({ organisation: action.organisation, error: error }))
        })
      );
    })
  ));

  createCustomer$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.createCustomer),
    switchMap((action) => {
      return this.accountService.addOrganistation(action.name, action.contactNumber, action.email).pipe(
        map(response => {
          this.toastService.showToast("Successfully created customer", ToastType.SUCCESS)
          return AccountActions.createCustomerSuccess({ organisation: response['organisation'] as IOrganisation})
        }),
        catchError(error => {
          this.toastService.showToast("Failed to create customer", ToastType.ERROR)
          return of(AccountActions.createCustomerError({ error: error }))
        })
      );
    })
  ));

  updateCustomer$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.updateCustomer),
    switchMap((action) => {
      return this.accountService.updateOrganisation({
        id: action.organisationId,
        name: action.name,
        contactNumber: action.contactNumber,
        email: action.email
      }).pipe(
        map(organisation => {
          this.toastService.showToast("Successfully updated customer", ToastType.SUCCESS)
          return AccountActions.updateCustomerSuccess({ organisation: organisation })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update customer", ToastType.ERROR)
          return of(AccountActions.updateCustomerError({ organisationId: action.organisationId, error: error }))
        })
      );
    })
  ));

  updateOrganisationLogo$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.updateOrganisationLogo),
    switchMap((action) => {
      return this.accountService.updateOrganisationLogo(action.organisationId, action.logoKey).pipe(
        map(organisation => {
          this.toastService.showToast("Successfully updated logo", ToastType.SUCCESS)
          return AccountActions.updateOrganisationLogoSuccess({ organisation: organisation })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update logo", ToastType.ERROR)
          return of(AccountActions.updateOrganisationLogoError({ organisationId: action.organisationId, error: error }))
        })
      );
    })
  ));

  deleteCustomer$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.deleteCustomer),
    switchMap((action) => {
      return this.accountService.deleteOrganisation(action.organisation).pipe(
        map(organisation => {
          this.toastService.showToast("Successfully deleted customer", ToastType.SUCCESS)
          return AccountActions.deleteCustomerSuccess({ organisation: organisation})
        }),
        catchError(error => {
          this.toastService.showToast("Failed to delete customer", ToastType.ERROR)
          return of(AccountActions.deleteCustomerError({ organisation: action.organisation, error: error }))
        })
      );
    })
  ));

  deleteUser$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.deleteUser),
    switchMap((action) => {
      return this.accountService.deleteUser(action.user).pipe(
        map(user => {
          this.toastService.showToast("Successfully deleted user", ToastType.SUCCESS)
          return AccountActions.deleteUserSuccess({ user: user})
        }),
        catchError(error => {
          this.toastService.showToast("Failed to delete user", ToastType.ERROR)
          return of(AccountActions.deleteUserError({ user: action.user, error: error }))
        })
      );
    })
  ));

  enableScoutType$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.enableScoutType),
    switchMap((action) => {
      return this.accountService.enableScoutType(action.organisationId, action.roleId, action.scoutType).pipe(
        map((response: any) => {
          const copilotUser: ICopilotUser = { ...response, copilot_type: response.roles?.[0]?.name };
          this.toastService.showToast("Scout enabled successfully", ToastType.SUCCESS);
          return AccountActions.enableScoutTypeSuccess({ organisationId: action.organisationId, copilotUser });
        }),
        catchError(error => {
          this.toastService.showToast("Failed to enable Scout", ToastType.ERROR);
          return of(AccountActions.enableScoutTypeFailure({ organisationId: action.organisationId, scoutType: action.scoutType, error }));
        })
      );
    })
  ));

  disableScoutType$ = createEffect(() => this.actions$.pipe(
    ofType(AccountActions.disableScoutType),
    mergeMap((action) => {
      return this.accountService.disableScoutType(action.copilotUserId).pipe(
        map(() => {
          this.toastService.showToast("Scout disabled successfully", ToastType.SUCCESS);
          return AccountActions.disableScoutTypeSuccess({ organisationId: action.organisationId, copilotUserId: action.copilotUserId });
        }),
        catchError(error => {
          this.toastService.showToast("Failed to disable Scout", ToastType.ERROR);
          return of(AccountActions.disableScoutTypeFailure({ organisationId: action.organisationId, copilotUserId: action.copilotUserId, error }));
        })
      );
    })
  ));

}